import React, { useEffect, useState } from 'react';
import * as _ from "lodash";
import MerchantDetailsComponentProps from '../merchantDetailsComponentProps';
import MobileAppConfiguration from './mobileAppConfiguration';
import merchantFileService from './merchantFileService';
import { Button, Col, Form, Row } from 'react-bootstrap';
import Loading from 'common/ui/Loading';
import MobileAppConfigurationEditor from './MobileAppConfigurationEditor';
import loggingService from 'common/logging/loggingService';
import notificationService from 'common/notificationService';
import AsyncButton from 'common/ui/AsyncButton';
import Merchant from 'domain/merchant';
import merchantService from '../merchantService';


const MobileAppSettings: React.FC<MerchantDetailsComponentProps> = props => {

    const [isLoaded, setIsLoaded] = useState(false);
    const [originalConfig, setOriginalConfig] = useState<MobileAppConfiguration>(null);
    const [config, setConfig] = useState<MobileAppConfiguration>(null);
    const [configAsJson, setConfigAsJson] = useState<string>(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [defaultsAdded, setDefaultsAdded] = useState(false);
    const [useJsonEditor, setUseJsonEditor] = useState(false);

    useEffect(() => {
        loadConfigFile();
    }, [props.merchant]);

    useEffect(() => {
        if (originalConfig == null) return;
        setConfig(_.defaultsDeep({}, originalConfig));
    }, [originalConfig]);

    useEffect(() => {
        if (config == null) return;

        setConfigAsJson(JSON.stringify(config, null, 4));
        setHasChanges(!_.isEqual(config, originalConfig));
    }, [config])

    async function loadConfigFile() {
        try {
            const c = await merchantFileService.getMobileAppConfig(props.merchant.id);
            
            const defaultConfig = getDefaultMobileAppConfiguration(props.merchant);
            if (c == null) setConfig(defaultConfig);
            else {
                const withDefaults = _.defaultsDeep({}, c, defaultConfig);
                //If we've added fields since the last time this was generated, show the save button as enabled
                if (!_.isEqual(c, withDefaults)) {
                    setDefaultsAdded(true);
                }
                setOriginalConfig(withDefaults);
            }
        }
        catch (err) {
            loggingService.error("Error loading mobile app config", err as Error);
            notificationService.error("Error loading mobile app config");
        }
        finally {
            setIsLoaded(true);
        }
    }

    function setConfigFromJson(jsonAsString: string) {
        try {
            setConfig(JSON.parse(jsonAsString));
        }
        catch (err) {
            loggingService.error("Error parsing JSON", err as Error);
            notificationService.error("Error parsing JSON");
        }
    }

    async function onSave(): Promise<any> {
        try {
            await merchantFileService.updateMobileAppConfig(props.merchant.id, config);
            setOriginalConfig(config);
            setDefaultsAdded(false);
            notificationService.success("Mobile app configuration updated!")
        }
        catch (err) {
            loggingService.error("Error updating mobile app config", err as Error);
            notificationService.error("Error updating mobile app configuration");
        }
    }

    async function updateMerchant(merchant: Merchant) {
        try {
            await merchantService.updateMerchant(merchant.id, { 
                merchantAppStoreUrl: merchant.appStoreUrl,
                merchantPlayStoreUrl: merchant.playStoreUrl 
            });
            notificationService.success("Mobile app stores updated!")
        }
        catch (err) {
            loggingService.error("Error updating app stores", err as Error);
            notificationService.error("Error updating app stores");
        }
    }

    function onReset() {
        setConfig(originalConfig);
    }

    if (!isLoaded) return <Loading />

    return <div className="mobile-app-config container-fluid">
            <h2>App Stores</h2>
            <Form.Group as={Row} className="m-1 mb-5">
            <Form.Label className="mt-1">App Store URL</Form.Label>
                <Col xs="4">
                    <Form.Control type="text"
                        defaultValue={props.merchant.appStoreUrl}
                        onBlur={(ev: React.ChangeEvent<HTMLInputElement>) => updateMerchant({...props.merchant, appStoreUrl: ev.target.value})}
                        placeholder="https://"
                    />
                </Col>

                <Form.Label className="ml-3 mt-1">Play Store URL</Form.Label>
                <Col xs="4">
                    <Form.Control type="text"
                        defaultValue={props.merchant.playStoreUrl}
                        onBlur={(ev: React.ChangeEvent<HTMLInputElement>) => updateMerchant({...props.merchant, playStoreUrl: ev.target.value})}
                        placeholder="https://"
                    />
                </Col>
            </Form.Group>

        <div className="row">
            <div className="col">
                {useJsonEditor && <Form.Control as="textarea" 
                    rows={20}
                    value={configAsJson}
                    onChange={(ev: any) => setConfigAsJson(ev.target.value)}
                    onBlur={(ev: any) => setConfigFromJson(ev.target.value)}
                    />}

                {!useJsonEditor && <MobileAppConfigurationEditor 
                    config={config}
                    onChange={m => setConfig(m)} />
                }
            </div>
            <div className="col">
                Use JSON Editor
                <Form.Switch id="use-json-toggle"
                    className="d-inline-block ml-2"
                    checked={useJsonEditor}
                    onChange={ev => setUseJsonEditor(ev.target.checked)}
                    />

                <AsyncButton className="mr-2" onClickAsync={onSave} disabled={!hasChanges && !defaultsAdded}>
                    Save
                </AsyncButton>
                <Button variant="secondary" onClick={onReset} disabled={!hasChanges}>
                    Reset
                </Button>
            </div>
        </div>
    </div>;
}

function getDefaultMobileAppConfiguration(merchant: Merchant): MobileAppConfiguration {
    return {
        id: "",
        name: merchant.name,
        email: merchant.email,
        phone: merchant.phone,
        websiteUrl: merchant.websiteUrl,
        address: merchant.mailingAddress,
        copyright: `Copyright © 2023 ${merchant.name}. All rights reserved`,
        features: {
            cashBids: {
                showStateCode: {
                    enabled: true
                }
            },
            settlements: { enabled: false },
            scaleTickets: { enabled: false },
            accountBalances: { enabled: false },
            eSign: { enabled: false },
            marketToggle: { enabled: true },
            autoInvite: { enabled: false }
        },
        client: merchant.name.replaceAll(' ', ''),
        appName: `${merchant.name}, Powered By StoneX Hedge`,
        displayName: merchant.name,
        logoUrl: "",
        appIconUrl: "",
        splashScreenUrl: "",
        theme: "Gray",
        apiKey: ""
    }
}

export default MobileAppSettings;